// Google Drive Picker helper.
//
// The Picker JS library is loaded on-demand the first time the user clicks
// "Import from Google Drive" — keeps the initial bundle slim and avoids a
// Google round-trip for users who never use the feature.
//
// Usage:
//   const file = await openDrivePicker({ accessToken, apiKey, appId });
//   if (!file) return;            // user cancelled
//   await api.post('/integrations/google/import', { file_ids: [file.id] });

const GAPI_SRC = "https://apis.google.com/js/api.js";

let gapiLoadPromise = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

async function ensureGapiPicker() {
  if (gapiLoadPromise) return gapiLoadPromise;
  gapiLoadPromise = (async () => {
    await loadScript(GAPI_SRC);
    await new Promise((resolve, reject) => {
      // eslint-disable-next-line no-undef
      window.gapi.load("picker", { callback: resolve, onerror: reject });
    });
  })();
  return gapiLoadPromise;
}

/**
 * Opens the Google Picker. Resolves with an array of selected file objects
 * (each `{id, name, mimeType, sizeBytes, lastEditedUtc}`) or [] if the user
 * cancels.
 *
 * Restricts the view to MIME types we know how to extract — PDF, DOCX, plain
 * text, and native Google Docs.
 */
export async function openDrivePicker({ accessToken, apiKey }) {
  if (!accessToken || !apiKey) {
    throw new Error("Missing accessToken or apiKey for Google Picker.");
  }
  await ensureGapiPicker();
  // eslint-disable-next-line no-undef
  const google = window.google;

  return new Promise((resolve, reject) => {
    try {
      const mimeTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "text/plain",
        "text/markdown",
        "application/vnd.google-apps.document",
        "application/vnd.google-apps.spreadsheet",
        "application/vnd.google-apps.presentation",
      ].join(",");

      const docsView = new google.picker.DocsView(google.picker.ViewId.DOCS)
        .setMimeTypes(mimeTypes)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false);

      const picker = new google.picker.PickerBuilder()
        .enableFeature(google.picker.Feature.NAV_HIDDEN)
        .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
        .setOAuthToken(accessToken)
        .setDeveloperKey(apiKey)
        .addView(docsView)
        .setTitle("Import from Google Drive")
        .setCallback((data) => {
          const action = data[google.picker.Response.ACTION];
          if (action === google.picker.Action.PICKED) {
            const docs = data[google.picker.Response.DOCUMENTS] || [];
            resolve(docs.map((d) => ({
              id: d.id,
              name: d.name,
              mimeType: d.mimeType,
              sizeBytes: d.sizeBytes,
            })));
          } else if (action === google.picker.Action.CANCEL) {
            resolve([]);
          }
        })
        .build();
      picker.setVisible(true);
    } catch (err) {
      reject(err);
    }
  });
}
